import ajax from './ajax';

// Shape returned by the backend (userService.sanitizeUser): nested profile
// plus a flat `name` compatibility field.
export interface User {
    _id: string;
    email: string;
    name: string;
    role: string;
    organizationId?: string;
    profile?: {
        name?: string;
        phone?: string;
        avatar?: string;
        timezone?: string;
        language?: string;
    };
    status: string;
    emailVerified: boolean;
    lastLogin?: string;
    createdAt: string;
    updatedAt: string;
}

export interface UserProfile extends User {
    totalExamsTaken?: number;
    totalAttempts?: number;
    averageScore?: number;
}

export interface UpdateProfileData {
    name?: string;
    profile?: {
        name?: string;
        phone?: string;
        avatar?: string;
        timezone?: string;
        language?: string;
    };
}

export interface UpdatePasswordData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export interface UserListParams {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface UserListResponse {
    users: User[];
    total: number;
    page: number;
    limit: number;
}

class UserService {
    async getCurrentUser(): Promise<UserProfile> {
        return ajax.get('/api/users/me');
    }

    async getUserById(id: string): Promise<User> {
        return ajax.get(`/api/users/${id}`);
    }

    async updateProfile(data: UpdateProfileData): Promise<UserProfile> {
        return ajax.put('/api/users/me', data);
    }

    async updatePassword(data: UpdatePasswordData): Promise<{ message: string }> {
        return ajax.put('/api/users/me/password', {
            oldPassword: data.currentPassword,
            newPassword: data.newPassword,
        });
    }

    async listUsers(params: UserListParams = {}): Promise<UserListResponse> {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, String(value));
            }
        });
        const query = queryParams.toString();
        return ajax.get(`/api/users${query ? `?${query}` : ''}`);
    }

    async updateUser(id: string, data: any): Promise<User> {
        return ajax.put(`/api/users/${id}`, data);
    }

    async deleteUser(id: string): Promise<{ message: string }> {
        return ajax.delete(`/api/users/${id}`);
    }

    async createUser(data: any): Promise<User> {
        return ajax.post('/api/users', data);
    }
}

export default new UserService();
